/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2026 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * Feature Flags client adapter over OpenFeature + Harness FME (Split provider).
 *
 * @file FeatureFlagsClient.js
 * @author Mohamed yasar arabath M
 * @since 1.0.11
 */

import { OpenFeature } from '@openfeature/web-sdk';
import CommonUtils from '../utils/CommonUtils';
import { ERROR, OFF } from '../constants';

/**
 * FeatureFlagsClient static class acts as a client adapter over OpenFeature + Harness FME (Split provider).
 */
export default class FeatureFlagsClient {
  /**
   * Stores the OpenFeature client instance.
   * @type {import('@openfeature/web-sdk').Client}
   */
  static client = null;

  /**
   * Initializes the FeatureFlagsClient with the OpenFeature client.
   * @param {import('@openfeature/web-sdk').Client} client
   */
  static setClient(client) {
    FeatureFlagsClient.client = client;
  }

  /**
   * Returns a LaunchDarkly-compatible interface using the FeatureFlagsClient.
   */
  static createClient(client) {
    FeatureFlagsClient.setClient(client);

    return {
      variation: FeatureFlagsClient.variation.bind(this),
      variationProperty: FeatureFlagsClient.variationProperty.bind(this),
      identify: FeatureFlagsClient.identify.bind(this),
      track: FeatureFlagsClient.track.bind(this)
    };
  }

  /**
   * Variation method to get feature flag value.
   * @param {string} key
   * @param {any} defaultValue
   * @returns {*}
   */
  static variation(key, defaultValue) {
    const client = FeatureFlagsClient.client;
    if (!client) throw new Error('FeatureFlagsClient not initialized');
    if (typeof defaultValue === 'boolean') {
      return client.getBooleanValue(key, defaultValue);
    }
    if (typeof defaultValue === 'number') {
      return client.getNumberValue(key, defaultValue);
    }
    if (typeof defaultValue === 'string') {
      const stringValue = client.getStringValue(key, defaultValue);
      return CommonUtils.localStringCompare(OFF, stringValue) ? '' : stringValue;
    }
    if (defaultValue !== null && typeof defaultValue === 'object') {
      const { flagMetadata: { config }, reason } = client.getStringDetails(key, defaultValue);
      let flagValue = defaultValue;
      if (!CommonUtils.localStringCompare(ERROR, reason) && config) {
        try {
          flagValue = JSON.parse(config);
        } catch (error) {
          flagValue = defaultValue;
        }
      }

      return flagValue;
    }

    return client.getStringValue(key, String(defaultValue));
  }

  /**
   * Returns a property value from a JSON object feature flag.
   * @param {string} key
   * @param {string} propertyName
   * @param {object} defaultObject
   * @returns {*}
   */
  static variationProperty(key, propertyName, defaultObject = {}) {
    const value = this.variation(key, defaultObject);
    if (value && typeof value === 'object') {
      return value[propertyName] || defaultObject[propertyName] || '';
    }

    return defaultObject[propertyName] || '';
  }

  /**
   * Identify method to set evaluation context.
   * @param {object} context
   * @param {function} callback
   */
  static identify(context, callback) {
    const { kind, key, ...rest } = context;
    const evaluationContext = {
      targetingKey: String(key),
      trafficType: kind,
      ...rest
    };

    OpenFeature.setContext(evaluationContext)
      .then(() => {
        if (typeof callback === 'function') {
          callback(null, true);
        }
      })
      .catch((err) => {
        if (typeof callback === 'function') {
          callback(err);
        }
      });
  }

  /**
   * Track custom events.
   * @param {string} name
   * @param {object} customFields
   */
  static track(name, customFields) {
    const client = FeatureFlagsClient.client;
    if (!client) throw new Error('FeatureFlagsClient not initialized');
    if (!name) return;
    client.track(name, {
      properties: customFields && typeof customFields === 'object' ? customFields : {}
    });
  }
}

export const createClient = FeatureFlagsClient.createClient.bind(FeatureFlagsClient);
